import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Checkbox } from 'shared/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Flame } from 'lucide-react'
import type { TemplateSubactivity, TemplateSubactivityField } from 'shared/api/templateService'
import { cn } from 'shared/ui/utils'
import { buildSubactivityValidationKey } from 'modules/permit/pages/permit-request/utils/validationTargets'

export interface SubActivitiesSectionProps {
  subActivities: TemplateSubactivity[]
  selectedActivities: string[]
  setSelectedActivities: (activities: string[]) => void
  extraFieldValues: Record<string, string>
  setExtraFieldValues: (values: Record<string, string>) => void
  getValidationContainerClass?: (key?: string | null) => string
}

export const buildSubactivityFieldKey = (subactivityId: string, fieldId: string) =>
  `${subactivityId}_${fieldId}`

const parseOptions = (optionsJson?: string | null) => {
  if (!optionsJson) return null

  try {
    const parsed = JSON.parse(optionsJson)

    if (Array.isArray(parsed)) {
      if (parsed.every((option) => typeof option === 'string')) {
        return parsed.map((value: string) => ({ value, label: value }))
      }

      if (
        parsed.every(
          (option) => option && typeof option === 'object' && 'value' in option && 'label' in option,
        )
      ) {
        return parsed as Array<{ value: string; label: string }>
      }
    }
  } catch (error) {
    console.warn('Unable to parse sub-activity options', error)
  }

  return null
}

export function SubActivitiesSection({
  subActivities,
  selectedActivities,
  setSelectedActivities,
  extraFieldValues,
  setExtraFieldValues,
  getValidationContainerClass,
}: SubActivitiesSectionProps) {
  /**
   * Toggle selected state for a sub-activity. When an activity is unselected we also purge any
   * stored field values to avoid posting stale data back to the server.
   */
  const handleActivityToggle = (subactivityId: string) => {
    const isSelected = selectedActivities.includes(subactivityId)

    if (isSelected) {
      const remaining = selectedActivities.filter((id) => id !== subactivityId)
      setSelectedActivities(remaining)

      const updatedValues = { ...extraFieldValues }
      const prefix = `${subactivityId}_`
      Object.keys(updatedValues).forEach((key) => {
        if (key.startsWith(prefix)) {
          delete updatedValues[key]
        }
      })
      setExtraFieldValues(updatedValues)
    } else {
      setSelectedActivities([...selectedActivities, subactivityId])
    }
  }

  const handleExtraFieldChange = (
    subactivityId: string,
    field: TemplateSubactivityField,
    value: string,
  ) => {
    const fieldKey = buildSubactivityFieldKey(subactivityId, field.subactivityFieldId)

    setExtraFieldValues({
      ...extraFieldValues,
      [fieldKey]: value,
    })
  }

  const renderExtraField = (subactivityId: string, field: TemplateSubactivityField) => {
    const fieldKey = buildSubactivityFieldKey(subactivityId, field.subactivityFieldId)
    const validationKey = buildSubactivityValidationKey(subactivityId, field.subactivityFieldId)
    const value = extraFieldValues[fieldKey] ?? ''
    const options = parseOptions(field.optionsJson)

    if (field.fieldType === 'select' && options?.length) {
      return (
        <div
          key={fieldKey}
          className={cn('space-y-2 rounded-lg', getValidationContainerClass?.(validationKey))}
          data-validation-key={validationKey}
        >
          <Label className="text-sm text-gray-600">
            {field.label} {field.required ? <span className="text-red-500">*</span> : null}
          </Label>
          <Select
            value={value || '_default'}
            onValueChange={(selected) =>
              handleExtraFieldChange(subactivityId, field, selected === '_default' ? '' : selected)
            }
          >
            <SelectTrigger className="border-gray-200 bg-gray-50">
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_default">Select {field.label.toLowerCase()}</SelectItem>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )
    }

    const inputType = field.fieldType === 'number' ? 'number' : 'text'

    return (
      <div
        key={fieldKey}
        className={cn('space-y-2 rounded-lg', getValidationContainerClass?.(validationKey))}
        data-validation-key={validationKey}
      >
        <Label className="text-sm text-gray-600">
          {field.label} {field.required ? <span className="text-red-500">*</span> : null}
        </Label>
        <Input
          type={inputType}
          value={value}
          onChange={(event) => handleExtraFieldChange(subactivityId, field, event.target.value)}
          className="border-gray-200 bg-gray-50"
          placeholder={`Enter ${field.label}`}
        />
      </div>
    )
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Flame className="h-5 w-5 text-orange-500" />
          Sub-Activities & Extra Inputs
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <Label className="text-sm text-gray-600">Select sub-activities (click to toggle)</Label>
          <div className="space-y-4 rounded-md border border-gray-200 p-4">
            {subActivities.length === 0 ? (
              <div className="text-sm text-gray-500">No sub-activities available for this permit type.</div>
            ) : (
              subActivities
                .slice()
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((activity) => {
                  const subactivityId = activity.subactivityId
                  const isSelected = selectedActivities.includes(subactivityId)

                  return (
                    <div key={subactivityId} className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id={subactivityId}
                          checked={isSelected}
                          onCheckedChange={() => handleActivityToggle(subactivityId)}
                        />
                        <Label htmlFor={subactivityId} className="cursor-pointer text-sm">
                          {activity.name} <span className="text-xs text-gray-500">({activity.code})</span>
                        </Label>
                      </div>

                      {isSelected && activity.fields.length > 0 ? (
                        <div className="ml-6 space-y-3">
                          {activity.fields
                            .slice()
                            .sort((a, b) => (a?.orderIndex ?? 0) - (b?.orderIndex ?? 0))
                            .map((field) => renderExtraField(subactivityId, field as TemplateSubactivityField))}
                        </div>
                      ) : null}
                    </div>
                  )
                })
            )}
          </div>
        </div>

        {selectedActivities.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            Select at least one sub-activity to see additional requirements.
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
